import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, RedirectCommand, ResolveFn, Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { catchError, finalize, map, of } from 'rxjs';
import {
  BrandSetting,
  DEFAULT_BRANDING,
} from '@app/brand-settings/brand-setting-service/brand-setting';
import { API_BASE_URL } from '@providers/api-config/api-config.token';
import { OrderSignerResponse } from '@shared/types/order-signer-response.interface';
import { SigningStepsService } from '@app/signing-steps/signing-steps-service/signing-steps.service';
import { OrderContextService } from '@services/order-context/order-context.service';
import { SignerPrefetchStateService } from '@services/signer-prefetch-state.service';
import { createLoadingAndClearAnimationHandler } from '@app/providers/order-data/loading-animation-handler/loading-animation.handler';
import { ERROR_ROUTE } from '@shared/types/error-route.const';
import { SigningErrorPagePath } from '@app/shared/types/signing-error-page-path.enum';
import { StepType } from '@app/signing-steps/step-type.enum';
import { PORTAL_BASE_URL } from '@providers/portal-config/portal-config.token';

export const legacySigningResolver: ResolveFn<RedirectCommand> = (
  route: ActivatedRouteSnapshot
) => {
  const router = inject(Router);
  const signerReference = route.queryParams['id'] as string | undefined;

  if (!signerReference) {
    return of(
      new RedirectCommand(router.parseUrl(`${ERROR_ROUTE}/${SigningErrorPagePath.NotFound}`))
    );
  }

  const portalBaseUrl = inject(PORTAL_BASE_URL);
  if (!signerReference.startsWith('r-')) {
    const decodedReference = atob(signerReference);
    const orderId = decodedReference.split('|')[0];

    window.location.href = `${portalBaseUrl}/signorder/index/${orderId}`;
    return of();
  }

  const http = inject(HttpClient);
  const apiBaseUrl = inject(API_BASE_URL);
  const brandSetting = inject(BrandSetting);
  const signingStepsService = inject(SigningStepsService);
  const orderContextService = inject(OrderContextService);
  const signerPrefetchState = inject(SignerPrefetchStateService);
  const cleanUpAnimation = createLoadingAndClearAnimationHandler();

  return http.post<OrderSignerResponse>(`${apiBaseUrl}/api/order/${signerReference}`, {}).pipe(
    map((response) => {
      brandSetting.setBrandConfig(response.brandingResponse);
      brandSetting.applyBrandColors();
      orderContextService.setOrderContext(
        response.orderId,
        response.signerId,
        response.loginIsMandatory,
        response.noSignatureRequired,
        response.isSignerFinished,
        response.isStepCompleted,
        {
          twoFactorPhoneNumber: response.twoFactorPhoneNumber,
          senderName: response.senderName,
          isFilesOrder: response.isFilesOrder,
          isForms: response.isForms,
        }
      );
      const nonReceiptSteps = response.steps.filter(
        (step) => step in StepType && (step as StepType) !== StepType.Receipt
      );

      if (nonReceiptSteps.length === 0) {
        return new RedirectCommand(
          router.parseUrl(`/order/${response.orderId}/signer/${response.signerId}/receipt`)
        );
      }

      signingStepsService.setStepsFromBackend(response.steps);
      signerPrefetchState.set(response);
      return new RedirectCommand(
        router.parseUrl(`/order/${response.orderId}/signer/${response.signerId}`)
      );
    }),
    catchError(() => {
      brandSetting.setBrandConfig(DEFAULT_BRANDING);
      brandSetting.applyBrandColors();
      return of(
        new RedirectCommand(router.parseUrl(`${ERROR_ROUTE}/${SigningErrorPagePath.Unexpected}`))
      );
    }),
    finalize(() => cleanUpAnimation())
  );
};
